import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { catchError, concatMap, map, toArray } from 'rxjs/operators';

export interface LigneGroupee { id: number; nom: string; info?: string; }
export interface ColonneGroupee { cle: string; libelle: string; unite?: string; entier?: boolean; max?: (l: LigneGroupee) => number | null; }
export interface ChampCommun { cle: string; libelle: string; type: 'date' | 'texte'; obligatoire?: boolean; placeholder?: string; }
/** Une écriture : rend un Observable par ligne ; `ref(cle)` donne une clé clientRef stable (nouvel essai sans doublon) */
export type TraitementGroupe = (ligne: LigneGroupee, valeurs: Record<string, number>, communs: Record<string, string>,
                                ref: (cle: string) => string) => Observable<unknown>[];

interface Etat { statut: 'attente' | 'ok' | 'erreur' | 'ignore'; message?: string; }

/**
 * Saisie groupée : une ligne par lot / troupeau / bassin, des colonnes chiffrées, des champs communs
 * (date…). Les envois partent un par un ; chaque ligne affiche son résultat ; « Réessayer » ne renvoie
 * que les lignes en erreur, avec les mêmes clés clientRef (aucun doublon côté serveur).
 */
@Component({
  selector: 'bay-saisie-groupee',
  template: `
    <div class="sg">
      <div class="row g-2 mb-2">
        <div class="col-sm-6" *ngFor="let ch of champsCommuns">
          <label class="bay-label" [for]="'sg-' + ch.cle">{{ ch.libelle }}</label>
          <input [id]="'sg-' + ch.cle" class="bay-input" [type]="ch.type === 'date' ? 'date' : 'text'" [(ngModel)]="communs[ch.cle]"
                 [placeholder]="ch.placeholder || ''" [disabled]="envoi">
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-sm align-middle sg-table">
          <thead><tr><th>{{ libelleLigne }}</th><th *ngFor="let c of colonnes" class="text-end">{{ c.libelle }}<span *ngIf="c.unite" class="text-muted"> ({{ c.unite }})</span></th><th></th></tr></thead>
          <tbody>
            <tr *ngFor="let l of lignes" [class.sg-ok]="etat[l.id]?.statut === 'ok'" [class.sg-ko]="etat[l.id]?.statut === 'erreur'">
              <td><strong>{{ l.nom }}</strong><div *ngIf="l.info" class="text-muted small">{{ l.info }}</div></td>
              <td *ngFor="let c of colonnes" class="text-end">
                <input type="number" min="0" [step]="c.entier ? 1 : 'any'" class="bay-input sg-input" [attr.aria-label]="c.libelle + ' — ' + l.nom"
                       [(ngModel)]="valeurs[l.id][c.cle]" [disabled]="envoi || etat[l.id]?.statut === 'ok'">
              </td>
              <td class="sg-etat small">
                <span *ngIf="etat[l.id]?.statut === 'ok'" class="text-success">✓ enregistré</span>
                <span *ngIf="etat[l.id]?.statut === 'erreur'" class="text-danger" [title]="etat[l.id]?.message || ''">✗ {{ etat[l.id]?.message }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div *ngIf="erreur" class="bay-hint text-danger mb-2" role="alert" data-etat="erreur">{{ erreur }}</div>
      <div *ngIf="bilan" class="bay-hint mb-2" data-etat="bilan">{{ bilan }}</div>
      <div class="d-flex gap-2">
        <button type="button" class="bay-btn bay-btn-primary bay-btn-sm" [disabled]="envoi" (click)="enregistrer()">
          {{ envoi ? 'Enregistrement…' : aDesErreurs ? 'Réessayer les lignes en erreur' : 'Enregistrer' }}</button>
        <button type="button" class="bay-btn bay-btn-ghost bay-btn-sm" [disabled]="envoi" (click)="fermer.emit(nbOk > 0)">{{ nbOk ? 'Terminer' : 'Annuler' }}</button>
      </div>
    </div>
  `,
  styles: [`
    .sg-table th { font-size: 12.5px; color: #707a6c; font-weight: 600; white-space: nowrap; }
    .sg-input { max-width: 110px; margin-left: auto; text-align: right; padding: 6px 8px; }
    .sg-ok td { background: #eef6ea; }
    .sg-ko td { background: #fef2f2; }
    .sg-etat { min-width: 110px; }
  `]
})
export class SaisieGroupeeComponent implements OnChanges {
  @Input() lignes: LigneGroupee[] = [];
  @Input() colonnes: ColonneGroupee[] = [];
  @Input() champsCommuns: ChampCommun[] = [{ cle: 'date', libelle: 'Date', type: 'date', obligatoire: true }];
  @Input() libelleLigne = 'Cohorte';
  @Input() traitement!: TraitementGroupe;
  /** true si au moins une ligne a été enregistrée (la page recharge ses données) */
  @Output() fermer = new EventEmitter<boolean>();

  valeurs: Record<number, Record<string, number | null>> = {};
  communs: Record<string, string> = {};
  etat: Record<number, Etat> = {};
  envoi = false;
  erreur: string | null = null;
  bilan: string | null = null;
  private refs: Record<string, string> = {};

  ngOnChanges(ch: SimpleChanges): void {
    if (ch.lignes || ch.colonnes) {
      this.valeurs = {};
      this.etat = {};
      this.refs = {};
      this.bilan = null;
      this.erreur = null;
      for (const l of this.lignes) {
        this.valeurs[l.id] = {};
        for (const c of this.colonnes) { this.valeurs[l.id][c.cle] = null; }
      }
      this.communs = { date: new Date().toISOString().slice(0, 10) };
    }
  }

  get nbOk(): number { return Object.values(this.etat).filter(e => e.statut === 'ok').length; }
  get aDesErreurs(): boolean { return Object.values(this.etat).some(e => e.statut === 'erreur'); }

  /** Valeurs saisies d'une ligne (cases vides ignorées) ; message si une valeur est invalide */
  private lire(l: LigneGroupee): { valeurs: Record<string, number>; probleme?: string } {
    const v: Record<string, number> = {};
    for (const c of this.colonnes) {
      const brut = this.valeurs[l.id][c.cle];
      if (brut === null || brut === undefined || (brut as any) === '') { continue; }
      const n = Number(brut);
      if (!isFinite(n) || n < 0) { return { valeurs: v, probleme: `${c.libelle} : valeur invalide` }; }
      if (c.entier && !Number.isInteger(n)) { return { valeurs: v, probleme: `${c.libelle} : nombre entier attendu` }; }
      const max = c.max ? c.max(l) : null;
      if (max !== null && n > max) { return { valeurs: v, probleme: `${c.libelle} : ${n} dépasse l'effectif (${max})` }; }
      if (n > 0) { v[c.cle] = n; }
    }
    return { valeurs: v };
  }

  enregistrer(): void {
    this.erreur = null;
    for (const ch of this.champsCommuns) {
      if (ch.obligatoire && !(this.communs[ch.cle] || '').trim()) { this.erreur = `${ch.libelle} : champ obligatoire.`; return; }
    }
    const aEnvoyer: { l: LigneGroupee; obs: Observable<unknown>[] }[] = [];
    for (const l of this.lignes) {
      if (this.etat[l.id]?.statut === 'ok') { continue; }
      const { valeurs, probleme } = this.lire(l);
      if (probleme) { this.etat[l.id] = { statut: 'erreur', message: probleme }; continue; }
      if (!Object.keys(valeurs).length) { this.etat[l.id] = { statut: 'ignore' }; continue; }
      aEnvoyer.push({ l, obs: this.traitement(l, valeurs, { ...this.communs }, cle => this.ref(l.id, cle)) });
    }
    if (!aEnvoyer.length) {
      this.erreur = this.aDesErreurs ? 'Corrigez les lignes en erreur.' : 'Saisissez au moins une valeur.';
      return;
    }
    this.envoi = true;
    from(aEnvoyer).pipe(
      concatMap(({ l, obs }) => from(obs).pipe(
        concatMap(o => o),
        toArray(),
        map(() => ({ id: l.id, ok: true, message: '' })),
        catchError((e: HttpErrorResponse) => of({ id: l.id, ok: false, message: e?.error?.message || 'échec, réessayez' })),
      )),
      toArray(),
    ).subscribe(res => {
      this.envoi = false;
      for (const r of res) { this.etat[r.id] = r.ok ? { statut: 'ok' } : { statut: 'erreur', message: r.message }; }
      const ko = res.filter(r => !r.ok).length;
      this.bilan = `${res.length - ko} ligne(s) enregistrée(s)` + (ko ? `, ${ko} en erreur : corrigez ou réessayez.` : '.');
    });
  }

  private ref(id: number, cle: string): string {
    const k = `${id}:${cle}`;
    if (!this.refs[k]) {
      const c: any = (window as any).crypto;
      this.refs[k] = c && c.randomUUID ? c.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
    }
    return this.refs[k];
  }
}
